// Inclusion+AI — Team Management (v3.11)
// Inclusion Head + Super Admin: create/delete team members (specialists & parents)
// and share their credentials to enable them to log in.
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Users, UserPlus, Copy, Check, Trash2, KeyRound, Eye, EyeOff, RefreshCw, ShieldCheck, Mail, AlertCircle, X } from "lucide-react";
import { useT } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const genPassword = (len = 12) => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  const symbols = "!@#$";
  let s = "";
  for (let i = 0; i < len - 1; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s + symbols[Math.floor(Math.random() * symbols.length)];
};

const Card = ({ children, className = "", ...rest }) => (
  <div className={`bg-white rounded-2xl border border-[#E5E2DB] p-4 sm:p-6 ${className}`} {...rest}>{children}</div>
);

const Btn = ({ variant = "primary", className = "", children, ...rest }) => {
  const v = {
    primary: "bg-[#2F5233] text-white hover:bg-[#274428]",
    outline: "bg-white text-[#2F5233] border border-[#2F5233] hover:bg-[#F0EFEB]",
    ghost: "bg-transparent text-[#4A5553] hover:bg-[#F0EFEB]",
    danger: "bg-red-600 text-white hover:bg-red-700",
  }[variant];
  return <button className={`px-4 py-2 rounded-lg text-sm font-medium transition min-h-[40px] ${v} ${className}`} {...rest}>{children}</button>;
};

const Input = ({ className = "", ...rest }) => (
  <input {...rest} className={`w-full px-4 py-2.5 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 focus:border-[#2F5233] outline-none ${className}`}/>
);

// Modal shown once after account creation OR password reset — shows credentials to copy
function CredentialsModal({ payload, onClose }) {
  const { lang } = useT();
  const [showPw, setShowPw] = useState(true);
  const [copied, setCopied] = useState("");
  const email = payload.email;
  const pw = payload.temporary_password;
  const shareText = lang === "ar"
    ? `بيانات دخول Inclusion+AI:\nالبريد: ${email}\nكلمة المرور المؤقتة: ${pw}\nالرابط: ${window.location.origin}/login\n\nيرجى تغيير كلمة المرور من الملف الشخصي عند أول تسجيل دخول.`
    : `Inclusion+AI Login Credentials:\nEmail: ${email}\nTemporary password: ${pw}\nURL: ${window.location.origin}/login\n\nPlease change your password after first sign-in.`;

  const copy = async (val, k) => {
    await navigator.clipboard.writeText(val);
    setCopied(k);
    setTimeout(() => setCopied(""), 1800);
  };

  const emailShare = () => {
    const subject = encodeURIComponent(lang === "ar" ? "بيانات دخول Inclusion+AI" : "Your Inclusion+AI login details");
    const body = encodeURIComponent(shareText);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const whatsappShare = () => {
    const text = encodeURIComponent(shareText);
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="team-credentials-modal">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-5 bg-gradient-to-br from-[#2F5233] to-[#3d6b41] text-white">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={22}/>
            <h3 className="text-lg font-bold">{lang === "ar" ? "تم إنشاء الحساب بنجاح" : "Account created successfully"}</h3>
          </div>
          <p className="text-xs opacity-90">{lang === "ar" ? "احرص على نسخ ومشاركة كلمة المرور الآن — لن تظهر مجدداً." : "Copy and share this password now — it will not be shown again."}</p>
        </div>

        <div className="p-5 space-y-3">
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-xs text-amber-900" data-testid="temp-pw-warning">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5"/>
            <span>{lang === "ar" ? "شارك هذه البيانات مع العضو عبر قناة آمنة (WhatsApp / إيميل خاص). لن يتمكن أحد من استرجاع كلمة المرور — فقط إعادة تعيينها." : "Share these credentials with the member over a secure channel (WhatsApp / private email). No one can retrieve the password later — only reset it."}</span>
          </div>

          <div>
            <label className="text-xs text-[#4A5553] mb-1 block">{lang === "ar" ? "البريد الإلكتروني" : "Email"}</label>
            <div className="flex gap-2">
              <input readOnly value={email} className="flex-1 px-3 py-2.5 rounded-lg bg-[#F0EFEB] border border-[#E5E2DB] font-mono text-sm" data-testid="cred-email"/>
              <Btn variant="outline" onClick={() => copy(email, "email")} data-testid="copy-email">{copied === "email" ? <Check size={14}/> : <Copy size={14}/>}</Btn>
            </div>
          </div>

          <div>
            <label className="text-xs text-[#4A5553] mb-1 block">{lang === "ar" ? "كلمة المرور المؤقتة" : "Temporary password"}</label>
            <div className="flex gap-2">
              <input readOnly value={showPw ? pw : "•".repeat(pw.length)} className="flex-1 px-3 py-2.5 rounded-lg bg-[#F0EFEB] border border-[#E5E2DB] font-mono text-sm tracking-wide" data-testid="cred-password"/>
              <Btn variant="outline" onClick={() => setShowPw(v => !v)} data-testid="toggle-pw-visibility">{showPw ? <EyeOff size={14}/> : <Eye size={14}/>}</Btn>
              <Btn variant="outline" onClick={() => copy(pw, "pw")} data-testid="copy-password">{copied === "pw" ? <Check size={14}/> : <Copy size={14}/>}</Btn>
            </div>
          </div>

          <div className="pt-2 border-t border-[#E5E2DB]">
            <p className="text-xs text-[#4A5553] mb-2">{lang === "ar" ? "شارك البيانات كاملة" : "Share full credentials"}</p>
            <div className="grid grid-cols-3 gap-2">
              <Btn variant="outline" onClick={() => copy(shareText, "full")} data-testid="copy-full">
                {copied === "full" ? <><Check size={14} className="inline ms-1"/>{lang === "ar" ? "تم" : "Copied"}</> : <><Copy size={14} className="inline ms-1"/>{lang === "ar" ? "نسخ الكل" : "Copy all"}</>}
              </Btn>
              <Btn variant="outline" onClick={emailShare} data-testid="share-email"><Mail size={14} className="inline ms-1"/>{lang === "ar" ? "إيميل" : "Email"}</Btn>
              <Btn variant="outline" onClick={whatsappShare} data-testid="share-whatsapp">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="inline ms-1"><path d="M17.5 14.4c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15s-.77.97-.94 1.17c-.17.2-.35.22-.65.07-.3-.15-1.27-.47-2.42-1.49-.9-.8-1.5-1.79-1.68-2.09-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37s-1.04 1.02-1.04 2.49 1.07 2.89 1.22 3.09c.15.2 2.1 3.21 5.09 4.5.71.31 1.27.49 1.7.63.71.23 1.36.19 1.87.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35zM12 2C6.48 2 2 6.48 2 12c0 1.85.5 3.58 1.38 5.06L2 22l5.13-1.35A9.96 9.96 0 0012 22c5.52 0 10-4.48 10-10S17.52 2 12 2z"/></svg>WhatsApp
              </Btn>
            </div>
          </div>
        </div>

        <div className="p-4 bg-[#F9F8F6] border-t border-[#E5E2DB] flex justify-end">
          <Btn onClick={onClose} data-testid="cred-modal-close">{lang === "ar" ? "إغلاق" : "Close"}</Btn>
        </div>
      </div>
    </div>
  );
}

export function TeamManagementPage({ user }) {
  const { lang } = useT();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState(null); // credentials modal
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: genPassword(), account_type: "specialist" });
  const [filter, setFilter] = useState("all"); // all | specialist | parent

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/team`);
      setTeam(data);
    } catch (e) {
      setError(e.response?.data?.detail || (lang === "ar" ? "تعذّر تحميل الفريق" : "Failed to load team"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = useMemo(() => filter === "all" ? team : team.filter(t => t.account_type === filter), [team, filter]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/team`, form);
      setCreds(data);
      setShowForm(false);
      setForm({ name: "", email: "", password: genPassword(), account_type: "specialist" });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || (lang === "ar" ? "تعذّر إنشاء الحساب" : "Failed to create account"));
    } finally { setBusy(false); }
  };

  const remove = async (u) => {
    const msg = lang === "ar"
      ? `هل تريد حقاً حذف الحساب «${u.name || u.email}»؟\nلن يتمكن من الدخول بعد الحذف.`
      : `Are you sure you want to delete the account "${u.name || u.email}"?\nThey will no longer be able to log in.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/team/${u.id}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || (lang === "ar" ? "فشل الحذف" : "Delete failed"));
    }
  };

  const resetPassword = async (u) => {
    const msg = lang === "ar"
      ? `سيتم إنشاء كلمة مرور مؤقتة جديدة للحساب «${u.name || u.email}» وسيتم إظهارها مرة واحدة فقط. المتابعة؟`
      : `A new temporary password will be generated for "${u.name || u.email}" and shown only once. Continue?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await axios.post(`${API}/team/${u.id}/reset-password`);
      setCreds({ ...data, name: u.name, account_type: u.account_type });
    } catch (err) {
      alert(err.response?.data?.detail || (lang === "ar" ? "فشل إعادة التعيين" : "Reset failed"));
    }
  };

  const RoleBadge = ({ type }) => (
    <span className={`inline-block text-[10px] font-bold px-2 py-1 rounded-full ${type === "specialist" ? "bg-[#769FCD]/20 text-[#3b6ba0]" : "bg-[#E8A317]/20 text-[#8a5b00]"}`}>
      {type === "specialist" ? (lang === "ar" ? "أخصائي" : "Specialist") : (lang === "ar" ? "ولي أمر" : "Parent")}
    </span>
  );

  return (
    <div className="space-y-6 fade-up" data-testid="team-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold font-heading">{lang === "ar" ? "الفريق والحسابات" : "Team & Accounts"}</h1>
          <p className="text-[#4A5553] mt-1 text-sm">
            {user?.account_type === "super_admin"
              ? (lang === "ar" ? "إدارة كل حسابات المنصة عبر جميع الأقسام." : "Manage all platform accounts across every department.")
              : (lang === "ar" ? "أنشئ حسابات الأخصائيين وأولياء الأمور في قسمك، وشارك بياناتهم للدخول." : "Create specialist and parent accounts in your department and share their login credentials.")}
          </p>
          {user?.department?.name && <p className="text-xs text-[#2F5233] font-medium mt-1" data-testid="dept-label">{user.department.name}</p>}
        </div>
        <Btn onClick={() => { setForm({ name: "", email: "", password: genPassword(), account_type: "specialist" }); setShowForm(true); }} data-testid="add-member-btn">
          <UserPlus size={16} className="inline ms-1"/>{lang === "ar" ? "إضافة عضو جديد" : "Add new member"}
        </Btn>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="team-filters">
        {[
          { key: "all", ar: "الكل", en: "All" },
          { key: "specialist", ar: "أخصائيون", en: "Specialists" },
          { key: "parent", ar: "أولياء أمور", en: "Parents" },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`team-filter-${f.key}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition ${filter === f.key ? "bg-[#2F5233] text-white border-[#2F5233]" : "bg-white border-[#E5E2DB] text-[#4A5553] hover:border-[#2F5233]"}`}>
            {lang === "ar" ? f.ar : f.en}
            <span className="ms-1 opacity-70 text-xs">({f.key === "all" ? team.length : team.filter(t => t.account_type === f.key).length})</span>
          </button>
        ))}
      </div>

      {/* Create form (inline card) */}
      {showForm && <Card className="border-2 border-[#2F5233]/30" data-testid="team-create-form-card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold font-heading flex items-center gap-2"><UserPlus size={18} className="text-[#2F5233]"/>{lang === "ar" ? "حساب جديد" : "New account"}</h2>
          <button onClick={() => setShowForm(false)} data-testid="close-form"><X size={20} className="text-[#4A5553]"/></button>
        </div>
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="team-create-form">
          <div>
            <label className="text-xs text-[#4A5553] mb-1 block">{lang === "ar" ? "نوع الحساب" : "Account type"}</label>
            <div className="grid grid-cols-2 gap-2">
              {["specialist", "parent"].map(k => (
                <button type="button" key={k} onClick={() => setForm(f => ({...f, account_type: k}))} data-testid={`type-${k}`}
                  className={`px-3 py-2.5 rounded-lg text-sm border transition ${form.account_type === k ? "bg-[#2F5233] text-white border-[#2F5233]" : "bg-white border-[#E5E2DB] text-[#4A5553]"}`}>
                  {k === "specialist" ? (lang === "ar" ? "أخصائي / معالج" : "Specialist") : (lang === "ar" ? "ولي أمر" : "Parent")}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-[#4A5553] mb-1 block">{lang === "ar" ? "الاسم الكامل" : "Full name"}</label>
            <Input required placeholder={lang === "ar" ? "أ. اسم العضو" : "Full name"} value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="input-name"/>
          </div>
          <div>
            <label className="text-xs text-[#4A5553] mb-1 block">{lang === "ar" ? "البريد الإلكتروني" : "Email"}</label>
            <Input required type="email" placeholder="user@example.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="input-email"/>
          </div>
          <div>
            <label className="text-xs text-[#4A5553] mb-1 block">{lang === "ar" ? "كلمة المرور المؤقتة" : "Temporary password"}</label>
            <div className="flex gap-2">
              <Input required minLength={8} value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="font-mono" data-testid="input-password"/>
              <Btn type="button" variant="outline" onClick={() => setForm(f => ({...f, password: genPassword()}))} data-testid="regen-password" title={lang === "ar" ? "توليد كلمة مرور قوية" : "Generate strong password"}><RefreshCw size={14}/></Btn>
            </div>
            <p className="text-[10px] text-[#4A5553] mt-1">{lang === "ar" ? "8 أحرف على الأقل. سيُطلب من العضو تغييرها بعد أول دخول." : "8+ characters. The member will be prompted to change it after first login."}</p>
          </div>
          {error && <p className="md:col-span-2 text-sm text-red-600" data-testid="form-error">{error}</p>}
          <div className="md:col-span-2 flex gap-2 pt-2">
            <Btn type="submit" disabled={busy} data-testid="submit-create">{busy ? "..." : (lang === "ar" ? "إنشاء الحساب وعرض البيانات" : "Create account & show credentials")}</Btn>
            <Btn type="button" variant="outline" onClick={() => setShowForm(false)}>{lang === "ar" ? "إلغاء" : "Cancel"}</Btn>
          </div>
        </form>
      </Card>}

      {/* Team list */}
      {loading ? <Card><p className="text-center text-[#4A5553] py-6">{lang === "ar" ? "جارٍ التحميل..." : "Loading..."}</p></Card> :
        filtered.length === 0 ? <Card><div className="text-center py-8">
          <Users size={40} className="mx-auto text-[#4A5553] mb-2 opacity-40"/>
          <p className="text-[#4A5553] text-sm">{lang === "ar" ? "لا يوجد أعضاء بعد. ابدأ بإضافة أخصائيين وأولياء أمور لقسمك." : "No members yet. Add specialists and parents to your department to get started."}</p>
        </div></Card> :
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="team-list">
          {filtered.map(u => (
            <Card key={u.id} className="hover:border-[#2F5233] transition" data-testid={`team-item-${u.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-[#1A201E] truncate">{u.name || u.email}</h3>
                    <RoleBadge type={u.account_type}/>
                    {u.must_change_password && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300" title={lang === "ar" ? "لم يغيّر كلمة المرور بعد" : "Password not changed yet"}>{lang === "ar" ? "مؤقتة" : "temp"}</span>}
                  </div>
                  <p className="text-xs text-[#4A5553] mt-1 font-mono truncate">{u.email}</p>
                  {u.created_at && <p className="text-[10px] text-[#4A5553] mt-1">{lang === "ar" ? "أُنشئ" : "Created"}: {new Date(u.created_at).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US")}</p>}
                </div>
                <div className="flex flex-col gap-1.5">
                  <button onClick={() => resetPassword(u)} data-testid={`reset-pw-${u.id}`}
                    className="p-2 rounded-lg hover:bg-[#F0EFEB] text-[#2F5233]" title={lang === "ar" ? "إعادة تعيين كلمة المرور" : "Reset password"}>
                    <KeyRound size={16}/>
                  </button>
                  <button onClick={() => remove(u)} data-testid={`delete-${u.id}`}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-600" title={lang === "ar" ? "حذف الحساب" : "Delete account"}>
                    <Trash2 size={16}/>
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>}

      {creds && <CredentialsModal payload={creds} onClose={() => setCreds(null)}/>}
    </div>
  );
}

export default TeamManagementPage;
